/**
 * [web/shell] Mobile navigation visibility, focus and scroll containment.
 * Owns drawer interaction only; routes, permissions and account actions stay with Sidebar.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export function useMobileNavigation(routeKey: string) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches,
  );
  const [open, setOpen] = useState(false);
  const navigationRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const previousRoute = useRef(routeKey);
  const closeNavigation = useCallback(() => {
    if (
      navigationRef.current?.contains(document.activeElement) &&
      triggerRef.current?.getClientRects().length
    ) {
      triggerRef.current.focus();
    }
    setOpen(false);
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 640px)');
    const update = () => {
      setIsMobile(media.matches);
      setOpen(false);
    };
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (previousRoute.current !== routeKey) {
      previousRoute.current = routeKey;
      closeNavigation();
    }
  }, [routeKey, closeNavigation]);

  useEffect(() => {
    if (!isMobile || !open) return;
    const navigation = navigationRef.current;
    if (!navigation) return;
    const main = document.querySelector<HTMLElement>('.main');
    const previousInert = main?.inert ?? false;
    const previousOverflow = document.body.style.overflow;
    if (main) main.inert = true;
    document.body.style.overflow = 'hidden';

    const focusable = () =>
      [
        ...navigation.querySelectorAll<HTMLElement>(
          'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]',
        ),
      ].filter((element) => element.getClientRects().length > 0 && !element.closest('[inert]'));
    const focusFrame = window.requestAnimationFrame(() => focusable()[0]?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeNavigation();
      }
      if (event.key !== 'Tab') return;
      const elements = focusable();
      const first = elements[0];
      const last = elements.at(-1);
      if (!navigation.contains(document.activeElement)) {
        event.preventDefault();
        first?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (main) main.inert = previousInert;
      if (triggerRef.current?.getClientRects().length) triggerRef.current.focus();
    };
  }, [isMobile, open, closeNavigation]);

  return {
    isMobile,
    mobileOpen: isMobile && open,
    setOpen,
    closeNavigation,
    navigationRef,
    triggerRef,
  };
}
